import { FC, useEffect, useRef } from 'react';

import { INFO_LETTERS_FROM_EMAIL, SYSTEM_NOTIFICATIONS_FROM_EMAIL } from 'utils/constants/settings';

import { Card, Flex, Switch, Text, useDebounce, VSpacingContainer } from '@hh.ru/magritte-ui';

import { useRequestCancel } from 'hooks/common/useRequestCancel';
import useTrls from 'hooks/translation/useTrls';

import { saveSubscriptionSettings } from 'components/SubscriptionSettings/saveSubscriptionSettings';

import { setInfoLetters, setSystemNotifications } from 'store/models/settings';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/SubscriptionSettings/SubscriptionSettings.module.less';

enum SubscriptionSettingsKeys {
    SystemNotifications = 'systemNotifications',
    InfoLetters = 'infoLetters',
}

const TrlKeys = {
    [SubscriptionSettingsKeys.SystemNotifications]: {
        title: 'SubscriptionSettings.SystemNotifications.title',
        description: 'SubscriptionSettings.SystemNotifications.description',
    },
    [SubscriptionSettingsKeys.InfoLetters]: {
        title: 'SubscriptionSettings.InfoLetters.title',
        description: 'SubscriptionSettings.InfoLetters.description',
    },
};

export const SubscriptionSettings: FC = () => {
    const { systemNotifications, infoLetters } = useSelector((state) => state.settings);

    const dispatch = useDispatch();
    const trls = useTrls();
    const { abortRequest, abortSignal } = useRequestCancel();

    const SUBSCRIPTION_SETTINGS = [
        {
            key: SubscriptionSettingsKeys.SystemNotifications,
            value: systemNotifications,
            setValue: setSystemNotifications,
        },
        {
            key: SubscriptionSettingsKeys.InfoLetters,
            value: infoLetters,
            setValue: setInfoLetters,
        },
    ];

    const debouncedSaveSubscriptionSettings = useRef(
        useDebounce((onError: () => void) => {
            abortRequest();
            void dispatch(saveSubscriptionSettings(onError, abortSignal()));
        }, 400)
    );

    useEffect(() => {
        const subscribe = new URL(window.location.href).searchParams.getAll('subscribe');
        if (subscribe.length > 0) {
            const subscriptionFromEmail = subscribe[0];
            switch (subscriptionFromEmail) {
                case SYSTEM_NOTIFICATIONS_FROM_EMAIL:
                    dispatch(setSystemNotifications(true));
                    void dispatch(saveSubscriptionSettings());
                    break;
                case INFO_LETTERS_FROM_EMAIL:
                    dispatch(setInfoLetters(true));
                    void dispatch(saveSubscriptionSettings());
                    break;
            }
        }
    }, [dispatch]);

    return (
        <VSpacingContainer default={16}>
            {SUBSCRIPTION_SETTINGS.map(({ key, value, setValue }) => (
                <Card padding={24} borderRadius={24} stretched borderWidth="default" borderStyle="phantom" key={key}>
                    <Flex gap={12}>
                        <VSpacingContainer default={12}>
                            <Text typography="title-4-semibold">{trls(TrlKeys[key].title)}</Text>
                            <Text typography="label-2-regular" style="secondary">
                                {trls(TrlKeys[key].description)}
                            </Text>
                        </VSpacingContainer>
                        <div className={styles.switcherContainer}>
                            <Switch
                                checked={value}
                                onClick={() => {
                                    dispatch(setValue(!value));
                                    debouncedSaveSubscriptionSettings.current(() => dispatch(setValue(value)));
                                }}
                                data-qa={`${key}-switcher`}
                            />
                        </div>
                    </Flex>
                </Card>
            ))}
        </VSpacingContainer>
    );
};
